import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { freeTextBoxes, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionTextfeldRichtig: Lesson = {
  id: "textfeld-richtig",
  title: "Wann ein Textfeld doch richtig ist",
  summary: "Die Ausnahmen von der Regel – und wie man sie sauber umsetzt.",
  durationMinutes: 8,
  learningGoals: [
    "Die legitimen Einsatzfälle für freie Textfelder benennen",
    "Ein Textfeld so einsetzen, dass es nicht zum Problem wird",
    "Wiederkehrende Elemente stattdessen ins Layout verlegen",
  ],
  steps: [
    {
      id: "legitime-faelle",
      title: "Drei Fälle, in denen ein Textfeld richtig ist",
      explanation: [
        "Die Regel „Platzhalter statt Textfeld“ ist keine Ideologie. Es gibt Inhalte, für " +
          "die das Layout keinen Platzhalter vorsieht und auch keinen vorsehen sollte.",
        "Der erste Fall ist die Quellenangabe unter einem Diagramm oder Bild: klein, " +
          "grau, direkt am Objekt. Der zweite ist eine Beschriftung, die zu einem " +
          "bestimmten Punkt in einer Grafik gehört – eine Zahl an einem Pfeil, ein " +
          "Hinweis an einer Landkarte. Der dritte ist ein bewusst gestaltetes Element auf " +
          "einer Sonderfolie, etwa ein großes Zitat auf einer ansonsten leeren Folie.",
        "Allen drei Fällen ist gemeinsam: Der Text gehört räumlich zu einem konkreten " +
          "Objekt oder zu genau dieser einen Folie. Er ist kein Titel und kein " +
          "Folieninhalt im Sinne der Gliederung – er soll dort auch gar nicht auftauchen.",
        "Der Gegentest ist einfach: Kommt derselbe Text auf mehreren Folien an derselben " +
          "Stelle vor, ist es kein Fall für ein Textfeld. Dann gehört er in den " +
          "Folienmaster oder in ein Layout.",
      ],
      keyPoints: [
        "Legitim: Quellenangabe, Beschriftung an einer Grafik, Gestaltungselement einer Sonderfolie.",
        "Nicht legitim: Titel, Aufzählungen, alles, was sich wiederholt.",
        "Gegentest: Kommt es mehrfach vor, gehört es ins Layout.",
      ],
    },
    {
      id: "sauber-einsetzen",
      title: "Ein Textfeld sauber einsetzen",
      explanation: [
        "Wenn schon ein Textfeld, dann ordentlich. Drei Dinge machen den Unterschied " +
          "zwischen einem beabsichtigten Element und einem Fremdkörper.",
        "Erstens: gruppieren. Ein Textfeld, das zu einem Bild gehört, wird zusammen mit " +
          "dem Bild markiert und über Strg + G gruppiert. Danach verrutscht die " +
          "Beschriftung nicht mehr, wenn jemand das Bild verschiebt.",
        "Zweitens: benennen. Im Auswahlbereich lässt sich ein Objekt per Doppelklick " +
          "umbenennen. Aus „TextBox 7“ wird „Quelle Umsatzdiagramm“. Wer die Folie später " +
          "bearbeitet – auch du selbst in einem halben Jahr – weiß dann sofort, wozu das " +
          "Feld gehört.",
        "Drittens: ausrichten. „Start“ → „Anordnen“ → „Ausrichten“ setzt das Textfeld " +
          "bündig zu dem Objekt, auf das es sich bezieht. Mit eingeschalteten " +
          "Führungslinien („Ansicht“ → „Führungslinien“) rastet es zusätzlich an den " +
          "Rändern der Platzhalter ein.",
      ],
      keyPoints: [
        "Strg + G gruppiert Textfeld und Objekt, Strg + Umschalt + G hebt die Gruppe auf.",
        "Im Auswahlbereich per Doppelklick sprechende Namen vergeben.",
        "Ansicht → Führungslinien lässt Objekte an Platzhalterkanten einrasten.",
      ],
      task: {
        instruction:
          "Falls du ein Textfeld brauchst, gib ihm im Auswahlbereich einen sprechenden " +
          "Namen. Genauso richtig ist es, ganz ohne freie Textfelder auszukommen.",
        hint: "Start → Anordnen → Auswahlbereich, dann Doppelklick auf den Namen.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Der Name und die Art einer Form",
              "1.4",
              "Öffne den Auswahlbereich und prüfe selbst, ob dort noch generische Namen wie „Textfeld 4“ stehen.",
            );
          }

          const textfelder = snapshot.slides.flatMap(freeTextBoxes);
          if (textfelder.length === 0) {
            return pass(
              "Es gibt kein freies Textfeld – damit ist die Aufgabe auf dem direktesten Weg erfüllt.",
            );
          }

          // PowerPoint vergibt automatisch Namen wie "TextBox 4" oder "Textfeld 4".
          // Ein eigener Name ist alles, was diesem Muster nicht entspricht.
          const ohneNamen = textfelder.filter((form) =>
            /^(textbox|textfeld)\s*\d*$/i.test((form.name ?? "").trim()),
          );

          if (ohneNamen.length === 0) {
            return pass(
              `Alle ${textfelder.length} Textfelder tragen einen eigenen Namen – die Folien bleiben nachvollziehbar.`,
            );
          }

          return fail(
            `${ohneNamen.length === 1 ? "Ein Textfeld trägt" : `${ohneNamen.length} Textfelder tragen`} noch den automatischen Namen, ` +
              `zum Beispiel „${ohneNamen[0].name}“ auf Folie ${ohneNamen[0].slideIndex + 1}.`,
            "Im Auswahlbereich doppelt auf den Namen klicken und beschreiben, wozu das Feld gehört – etwa „Quelle Umsatzdiagramm“.",
          );
        },
      },
    },
  ],
};
